"use client";
/** A readable name for a point from our own vocabulary: the residence
 *  when the pin sits on one, else the bina.az place. The field used to
 *  show "40.3717, 49.8358" when nothing else resolved (owner, 2026-10-10:
 *  "it is showing me the coordinate, not the address"). */
import { fetchAPI } from "@/lib/api";

/** GET /listings/locations/nearest: the place a point sits in. */
export interface NearestPlace {
  location_name: string | null;
  location_full_name: string | null;
  location_id: string | null;
  /** rayon | qesebe | metro | nisangah */
  location_type?: string | null;
  distance_km: number | null;
  /** The rayon polygon around the point (null outside the city's). */
  rayon?: string | null;
  /** Set only when the point is on the residence (its centroid within
   *  250 m): the calculator snaps a pin to it by the same rule. */
  complex_name: string | null;
  complex_slug: string | null;
}

export async function nearestPlace(lat: number, lng: number): Promise<NearestPlace | null> {
  try {
    return await fetchAPI<NearestPlace>("/listings/locations/nearest", { lat: String(lat), lng: String(lng) });
  } catch {
    return null;
  }
}

/** The label a point gets: the residence, else the place's full name. */
export function labelOfNearest(d: NearestPlace | null): string | null {
  if (!d) return null;
  const label = (d.complex_name || d.location_full_name || d.location_name || "").trim();
  return label || null;
}

export async function nearestPlaceLabel(lat: number, lng: number): Promise<string | null> {
  return labelOfNearest(await nearestPlace(lat, lng));
}

/** "40.3717, 49.8358": a label that is only the coordinates. */
export const isCoordinateLabel = (label: string | null | undefined): boolean =>
  !!label && /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(label.trim());
